import { useEffect, useRef } from "react";
import moment, { Moment } from "moment-timezone";
import { useIntl } from "react-intl";
import IDeliveryDateRules from "../../../models/IDeliveryDateRules";
import OwDateInput from "../OwDateInput/OwDateInput";
import { DAY_ABBR_KEYS, format12h, toDeliveryDateKey } from "../../business/BusinessSettings/DeliveryDeadlines/deliveryDeadlineUtils";
import {
    getEarliestFromRules,
    isSelectableCalendarDay,
    isSelectableDate,
} from "../../business/BusinessSettings/DeliveryDeadlines/deliveryDateRulesUtils";
import "./CustomerDeliveryDatePicker.scss";

type Props = {
    /** The account rules attached to the cart / order payload. Renders nothing without them. */
    rules?: IDeliveryDateRules | null;
    /** The delivery date currently on the cart / order (YYYY-MM-DD or an ISO UTC-midnight timestamp). */
    selectedDate?: string | Date | null;
    /** Called with YYYY-MM-DD when the customer picks a date, and once with the earliest date when none (or an invalid one) is set. */
    onSelectDate: (deliveryDate: string) => void;
    /** Disables the picker (a save in flight, a read-only order). */
    isReadOnly?: boolean;
    /**
     * Internal users may set any date (the admin override): the calendar is not restricted, a date the rules
     * would reject is kept rather than replaced, and an empty date is left empty (never auto-defaulted, so a
     * cleared date stays cleared; the API stamps the earliest date at submit if it is still empty). The
     * earliest-date hint still shows what customers get.
     */
    allowOverride?: boolean;
    /** The earliest date from a server rejection (ERROR_DELIVERY_DATE_NOT_SELECTABLE), shown until the next pick. */
    rejectedEarliestDate?: string | null;
};

/**
 * "Customer selects date" checkout control (OR-1531): a date picker that defaults to the earliest
 * selectable delivery date, disables dates before it and any blocked day of week, and explains the
 * account's cutoff / lead time / closed days. Same rules as the API validates at submit; the earliest
 * date is recomputed on every render so a cutoff passing while the page sits open rolls it forward.
 */
export const CustomerDeliveryDatePicker = (props: Props) => {
    const intl = useIntl();
    const { rules, onSelectDate, isReadOnly, allowOverride } = props;

    const earliest = rules ? getEarliestFromRules(rules) : null;
    const selectedKey = toDeliveryDateKey(props.selectedDate);
    const selectedIsValid = !!(rules && selectedKey && isSelectableDate(selectedKey, rules));
    // A customer's stale / missing date is replaced by the earliest date; an internal user's date (or lack
    // of one) is kept as-is.
    const fallbackKey = earliest && !selectedIsValid && !allowOverride ? earliest : null;
    const displayKey = fallbackKey || selectedKey || (allowOverride ? null : earliest);

    // Persist the fallback once per (stale, earliest) pair so the cart / order carries what is displayed;
    // the latest callback lives in a ref because callers recreate it every render.
    const onSelectDateRef = useRef(onSelectDate);
    useEffect(() => {
        onSelectDateRef.current = onSelectDate;
    }, [onSelectDate]);
    const reconciledRef = useRef<string | null>(null);
    useEffect(() => {
        if (!fallbackKey) {
            reconciledRef.current = null;
            return;
        }
        if (isReadOnly) return;
        const pair = `${selectedKey || ""}:${fallbackKey}`;
        if (reconciledRef.current === pair) return;
        reconciledRef.current = pair;
        onSelectDateRef.current(fallbackKey);
    }, [selectedKey, fallbackKey, isReadOnly]);

    if (!rules || !earliest) {
        return null;
    }

    const tz = rules.timezone;
    const blockedDays = (rules.blockedDays || []).map((day) => intl.formatMessage({ id: DAY_ABBR_KEYS[day] })).join(", ");
    const earliestLabel = moment.tz(earliest, "YYYY-MM-DD", tz).format("ddd, MMM D");
    const cutoffLabel = rules.cutoffTime ? `${format12h(rules.cutoffTime)} ${moment.tz(tz).format("z")}` : null;

    const onPick = (date: Moment) => {
        // OwDateInput (not isUTC) hands back a moment built from the picked calendar components, so the
        // key is the day the customer clicked whatever the browser timezone.
        onSelectDate(date.format("YYYY-MM-DD"));
    };

    return (
        <div className="CustomerDeliveryDatePicker rounded p-2 mb-2 small">
            <div className="fw-bold mb-1">
                <i className="fas fa-calendar-day me-1" />
                {intl.formatMessage({ id: "delivery_date" })}
                {!allowOverride && <span className="text-danger ms-1">*</span>}
            </div>
            <OwDateInput
                selectedDate={displayKey}
                isUTC={false}
                disabled={!!isReadOnly}
                fn={onPick}
                filterDate={allowOverride ? undefined : (day: Date) => isSelectableCalendarDay(day, rules)}
            />
            {props.rejectedEarliestDate && (
                <div className="delivery-date-error fw-bold mb-1" role="alert">
                    {intl.formatMessage(
                        { id: "delivery_date_no_longer_available" },
                        { date: moment.tz(props.rejectedEarliestDate, "YYYY-MM-DD", tz).format("ddd, MMM D") }
                    )}
                </div>
            )}
            <div>
                <span className="fw-bold">{intl.formatMessage({ id: "earliest_delivery_date" })}: </span>
                <span>{earliestLabel}</span>
            </div>
            {cutoffLabel && (
                <div className="text-muted">
                    {intl.formatMessage({ id: "delivery_date_cutoff_hint" }, { cutoff: cutoffLabel, days: rules.leadDays })}
                </div>
            )}
            {blockedDays && (
                <div className="text-muted">
                    <span className="fw-bold">{intl.formatMessage({ id: "no_delivery_on" })}: </span>
                    <span>{blockedDays}</span>
                </div>
            )}
            {allowOverride && selectedKey && !selectedIsValid && (
                <div className="text-muted fst-italic">{intl.formatMessage({ id: "delivery_date_override_hint" })}</div>
            )}
        </div>
    );
};

export default CustomerDeliveryDatePicker;
